import { useState, useEffect, useCallback } from "react";
import { authHeaders } from "../api";

function PnlBadge({ pct }) {
  if (pct == null) return null;
  const color = pct > 0 ? "#00E5C0" : pct < 0 ? "#E03030" : "#555E6E";
  return (
    <span style={{ color, fontSize: 12, fontWeight: 700 }}>
      {pct > 0 ? "+" : ""}{pct.toFixed(2)}%
    </span>
  );
}

function ChainBadge({ chain }) {
  const colors = { BSC: "#F0B90B", SOLANA: "#9945FF", ETH: "#627EEA" };
  return (
    <span style={{
      fontSize: 8, fontWeight: 700, letterSpacing: 1,
      color: colors[chain] || "#555E6E",
      border: `1px solid ${colors[chain] || "#555E6E"}44`,
      borderRadius: 3, padding: "2px 6px",
    }}>{chain}</span>
  );
}

function SignalBadge({ signal }) {
  const map = { BUY: ["#00E5C0", "#00E5C011"], SELL: ["#E03030", "#E0303011"], HOLD: ["#555E6E", "#55566E11"] };
  const [color, bg] = map[signal] || map.HOLD;
  return (
    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 1, color, background: bg,
      border: `1px solid ${color}44`, borderRadius: 3, padding: "2px 6px" }}>
      {signal || "HOLD"}
    </span>
  );
}

export default function PositionsView() {
  const [positions, setPositions] = useState(null);
  const [selling, setSelling] = useState(null);
  const [checking, setChecking] = useState(null);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    const load = () =>
      fetch("/api/positions", { headers: authHeaders() }).then(r => r.json()).then(setPositions).catch(() => setPositions([]));
    const loadWallet = () =>
      fetch("/api/wallet", { headers: authHeaders() }).then(r => r.json()).then(setWallet).catch(() => {});
    load(); loadWallet();
    const t = setInterval(() => { load(); loadWallet(); }, 10_000);
    return () => clearInterval(t);
  }, []);

  const handleSell = async (pos) => {
    if (!window.confirm(`Vendre ${pos.symbol} maintenant ?`)) return;
    setSelling(pos._id);
    try {
      const r = await fetch(`/api/positions/${pos._id}/sell`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await r.json();
      if (!data.success && data.error) alert(`Erreur: ${data.error}`);
    } catch (e) {
      alert("Erreur réseau");
    }
    setSelling(null);
    fetch("/api/positions", { headers: authHeaders() }).then(r => r.json()).then(setPositions);
  };

  const reload = useCallback(() =>
    fetch("/api/positions", { headers: authHeaders() }).then(r => r.json()).then(setPositions).catch(() => {}), []);

  const handleLock = async (pos) => {
    await fetch(`/api/positions/${pos._id}/lock`, { method: "POST", headers: authHeaders() });
    reload();
  };

  const handleCheck = async (pos) => {
    setChecking(pos._id);
    await fetch(`/api/positions/${pos._id}/check`, { headers: authHeaders() });
    reload();
    setChecking(null);
  };

  const handleClose = async (pos) => {
    if (!window.confirm(`Marquer ${pos.symbol} comme clôturé sans transaction ?`)) return;
    setSelling(pos._id);
    try {
      await fetch(`/api/positions/${pos._id}/close`, { method: "POST", headers: authHeaders() });
    } catch (e) {
      alert("Erreur réseau");
    }
    setSelling(null);
    fetch("/api/positions", { headers: authHeaders() }).then(r => r.json()).then(setPositions);
  };

  if (!positions) return (
    <div style={{ textAlign: "center", padding: 60, color: "#2A3040", fontSize: 11 }}>
      Chargement...
    </div>
  );

  // Valeur des positions ouvertes
  const totalInvested  = positions.reduce((s, p) => s + (p.buy_amount_usdt || 0), 0);
  const positionsCurrent = positions.reduce((s, p) => {
    const entry   = p.buy_price || p.price_at_detection || 0;
    const current = p.price_current || entry;
    const stake   = p.buy_amount_usdt || 0;
    if (!entry || !stake) return s;
    return s + stake * (current / entry);
  }, 0);
  const walletUsdt  = wallet && !wallet.error ? (wallet.usdt  || 0) : 0;
  const walletBnbUsd = wallet && !wallet.error ? (wallet.bnb_usd || 0) : 0;
  const grandTotal  = positionsCurrent + walletUsdt + walletBnbUsd;
  const totalPnlUsd = positionsCurrent - totalInvested;
  const totalPnlPct = totalInvested > 0 ? (totalPnlUsd / totalInvested) * 100 : 0;
  const pnlColor    = totalPnlUsd >= 0 ? "#00E5C0" : "#E03030";

  return (
    <div>
      {/* Bandeau portefeuille global */}
      <div style={{
        background: "#0D1117", border: "1px solid #1C2030", borderRadius: 12,
        padding: "20px 28px", marginBottom: 20,
      }}>
        {/* Ligne du haut : total + P&L */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 9, color: "#2A3040", letterSpacing: 2, marginBottom: 6 }}>VALEUR TOTALE DU PORTEFEUILLE</div>
            <div style={{ fontSize: 36, fontWeight: 800, color: "#E0E4F0", fontFamily: "'Syne',sans-serif", letterSpacing: -1 }}>
              ${grandTotal.toFixed(2)}
            </div>
          </div>
          {totalInvested > 0 && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 9, color: "#2A3040", letterSpacing: 2, marginBottom: 6 }}>P&L POSITIONS</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: pnlColor, fontFamily: "'Syne',sans-serif" }}>
                {totalPnlUsd >= 0 ? "+" : ""}{totalPnlUsd.toFixed(2)} $
              </div>
              <div style={{ fontSize: 11, color: pnlColor, marginTop: 2 }}>
                {totalPnlPct >= 0 ? "+" : ""}{totalPnlPct.toFixed(2)}%
              </div>
            </div>
          )}
        </div>

        {/* Ligne du bas : décomposition */}
        <div style={{ display: "flex", gap: 24, borderTop: "1px solid #111520", paddingTop: 14 }}>
          <div>
            <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>POSITIONS</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#E0E4F0" }}>${positionsCurrent.toFixed(2)}</div>
            <div style={{ fontSize: 9, color: "#555E6E" }}>investi ${totalInvested.toFixed(2)}</div>
          </div>
          <div style={{ width: 1, background: "#111520" }} />
          <div>
            <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>USDT</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#E0E4F0" }}>${walletUsdt.toFixed(2)}</div>
          </div>
          <div style={{ width: 1, background: "#111520" }} />
          <div>
            <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>BNB</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#F0B90B" }}>
              {wallet ? (wallet.bnb ?? 0).toFixed(4) : "—"} BNB
            </div>
            <div style={{ fontSize: 9, color: "#555E6E" }}>${walletBnbUsd.toFixed(2)}</div>
          </div>
          {wallet?.wallet && (
            <div style={{ marginLeft: "auto", alignSelf: "flex-end" }}>
              <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>WALLET</div>
              <div style={{ fontSize: 9, color: "#555E6E", fontFamily: "'IBM Plex Mono',monospace" }}>
                {wallet.wallet.slice(0, 8)}…{wallet.wallet.slice(-6)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#E0E4F0" }}>POSITIONS OUVERTES</span>
        <span style={{
          fontSize: 10, color: "#00E5C0", background: "#00E5C011",
          border: "1px solid #00E5C033", borderRadius: 10, padding: "2px 8px",
        }}>{positions.length}</span>
      </div>

      {positions.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#2A3040", fontSize: 11 }}>
          Aucune position ouverte
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {positions.map(pos => {
            const entry   = pos.buy_price || pos.price_at_detection || 0;
            const current = pos.price_current || entry;
            const highest = pos.highest_price || entry;
            const pnlPct  = entry ? (current - entry) / entry * 100 : null;
            const slLevel = entry * 0.98;
            const trailingLevel = highest * 0.95;
            const buyAmt  = pos.buy_amount_usdt;
            const txHash  = pos.buy_tx;
            const sellable = pos.sellable;
            const sellFails = pos.sell_fail_count || 0;
            const currentValue = buyAmt && entry ? buyAmt * (current / entry) : null;

            const borderColor = pos.locked ? "#E5A00066"
              : sellFails >= 3 ? "#E0303066"
              : "#1C2030";

            return (
              <div key={pos._id} style={{
                background: "#0D1117", border: `1px solid ${borderColor}`,
                borderRadius: 10, padding: "16px 20px",
              }}>
                {/* Alerte vente échouée */}
                {sellFails >= 1 && (
                  <div style={{
                    background: "#E0303011", border: "1px solid #E0303044",
                    borderRadius: 6, padding: "7px 12px", marginBottom: 10,
                    fontSize: 10, color: "#E03030", display: "flex", alignItems: "center", gap: 8,
                  }}>
                    ⚠ Vente échouée {sellFails}×
                    {pos.sell_error && <span style={{ color: "#555E6E" }}>— {pos.sell_error}</span>}
                    {sellable && !sellable.sellable && (
                      <span style={{ marginLeft: "auto", color: "#E5A000" }}>⛔ {sellable.reason}</span>
                    )}
                  </div>
                )}
                {pos.locked && (
                  <div style={{
                    background: "#E5A00011", border: "1px solid #E5A00044",
                    borderRadius: 6, padding: "7px 12px", marginBottom: 10,
                    fontSize: 10, color: "#E5A000",
                  }}>
                    🔒 Position verrouillée — auto-sell désactivé
                  </div>
                )}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: "#E0E4F0" }}>{pos.symbol}</span>
                    <ChainBadge chain={pos.chain} />
                    <SignalBadge signal={pos.trend_signal} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {currentValue != null && (
                      <span style={{ fontSize: 12, color: "#E0E4F0", fontWeight: 600 }}>
                        ${currentValue.toFixed(2)}
                      </span>
                    )}
                    <PnlBadge pct={pnlPct} />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>ENTRÉE</div>
                    <div style={{ fontSize: 11, color: "#E0E4F0" }}>${entry.toPrecision(4)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>ACTUEL</div>
                    <div style={{ fontSize: 11, color: "#E0E4F0" }}>${current.toPrecision(4)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>MISE</div>
                    <div style={{ fontSize: 11, color: "#E0E4F0" }}>{buyAmt ? `$${buyAmt}` : "—"}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>STOP LOSS</div>
                    <div style={{ fontSize: 11, color: "#E03030" }}>${slLevel.toPrecision(4)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>TRAILING</div>
                    <div style={{ fontSize: 11, color: "#E5A000" }}>${trailingLevel.toPrecision(4)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1, marginBottom: 3 }}>PIC</div>
                    <div style={{ fontSize: 11, color: "#555E6E" }}>${highest.toPrecision(4)}</div>
                  </div>
                </div>

                {txHash && (
                  <div style={{ borderTop: "1px solid #111520", paddingTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 8, color: "#2A3040", letterSpacing: 1 }}>TX</span>
                    <a
                      href={`https://bscscan.com/tx/${txHash}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 9, color: "#00E5C0", textDecoration: "none", fontFamily: "'IBM Plex Mono',monospace" }}
                    >
                      {txHash.slice(0, 16)}…{txHash.slice(-8)}
                    </a>
                    {pos.bought_at && (
                      <span style={{ fontSize: 9, color: "#2A3040", marginLeft: "auto" }}>
                        {new Date(pos.bought_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
                <div style={{ borderTop: "1px solid #111520", paddingTop: 10, marginTop: 4, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  {/* Gauche : check vendabilité + lock */}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => handleCheck(pos)}
                      disabled={checking === pos._id}
                      title="Vérifier si vendable sur PancakeSwap"
                      style={{
                        background: "transparent", border: "1px solid #00E5C033",
                        color: "#00E5C0", borderRadius: 5, padding: "5px 10px",
                        fontSize: 9, fontFamily: "'IBM Plex Mono',monospace",
                        letterSpacing: 1, cursor: checking === pos._id ? "wait" : "pointer",
                        opacity: checking === pos._id ? 0.5 : 1,
                      }}
                    >
                      {checking === pos._id ? "..." : "CHECK"}
                    </button>
                    {sellable && (
                      <span style={{
                        fontSize: 9, padding: "5px 8px", borderRadius: 5,
                        color: sellable.sellable ? "#00E5C0" : "#E03030",
                        background: sellable.sellable ? "#00E5C011" : "#E0303011",
                        border: `1px solid ${sellable.sellable ? "#00E5C033" : "#E0303033"}`,
                      }}>
                        {sellable.sellable ? `✓ $${sellable.usdt_out}` : `✗ ${sellable.reason}`}
                      </span>
                    )}
                    <button
                      onClick={() => handleLock(pos)}
                      title={pos.locked ? "Déverrouiller (auto-sell actif)" : "Verrouiller (bloquer auto-sell)"}
                      style={{
                        background: pos.locked ? "#E5A00022" : "transparent",
                        border: `1px solid ${pos.locked ? "#E5A000" : "#555E6E44"}`,
                        color: pos.locked ? "#E5A000" : "#555E6E",
                        borderRadius: 5, padding: "5px 10px",
                        fontSize: 9, fontFamily: "'IBM Plex Mono',monospace",
                        letterSpacing: 1, cursor: "pointer",
                      }}
                    >
                      {pos.locked ? "🔒 LOCK" : "🔓 LOCK"}
                    </button>
                  </div>
                  {/* Droite : fermer + vendre */}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => handleClose(pos)}
                      disabled={selling === pos._id}
                      style={{
                        background: "transparent", border: "1px solid #555E6E44",
                        color: "#555E6E", borderRadius: 5, padding: "5px 14px",
                        fontSize: 9, fontFamily: "'IBM Plex Mono',monospace",
                        letterSpacing: 1, cursor: selling === pos._id ? "wait" : "pointer",
                        opacity: selling === pos._id ? 0.5 : 1,
                      }}
                    >
                      {selling === pos._id ? "..." : "FERMER"}
                    </button>
                    <button
                      onClick={() => handleSell(pos)}
                      disabled={selling === pos._id}
                      style={{
                        background: "transparent", border: "1px solid #E0303066",
                        color: "#E03030", borderRadius: 5, padding: "5px 14px",
                        fontSize: 9, fontFamily: "'IBM Plex Mono',monospace",
                        letterSpacing: 1, cursor: selling === pos._id ? "wait" : "pointer",
                        opacity: selling === pos._id ? 0.5 : 1,
                      }}
                    >
                      {selling === pos._id ? "..." : "VENDRE"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
